import { DT, PF_HH, PF_HW } from '../constants';
import type { PartDef } from '../defs';
import { clampDifficulty, ENEMY_HP_MUL } from '../difficulty';
import { Ev } from '../events';
import { DEG } from '../math';
import type { World } from '../World';
import { placeObstacles } from './obstaclePlacement';

/* ═══════════════════════════════════════════════════════════════════════════
   Le dérouleur de niveau.

   Il remplace le générateur de charge du J1. Sa seule tâche : lire la timeline
   du `LevelDef`, activer les briques de vague à l'heure dite, et faire
   apparaître les ennemis qu'elles décrivent.

   Il ne connaît AUCUN ennemi en particulier. Ajouter un archétype ne demande
   pas d'y toucher — c'est le critère de fin du jalon.

   Sept couloirs, et les couloirs 0 et 6 restent des pièges de bord : le
   registre refuse au chargement d'y placer un ennemi de plus de 200 PV.
   ═══════════════════════════════════════════════════════════════════════════ */

const LANES = 7;

export function laneX(lane: number): number {
  return -PF_HW + ((lane + 0.5) * (PF_HW * 2)) / LANES;
}

export function levelSystem(w: World): void {
  const run = w.run;
  if (!run.levelId || run.finished) return;

  const level = w.defs.levels.get(run.levelId);
  if (!level) return;

  const prev = run.t;
  run.t += DT;

  // Activation des blocs — comparaison sur l'intervalle traversé, jamais sur
  // une égalité : à pas fixe, un `=== at` ne se produirait quasiment jamais.
  for (let i = run.nextBlock; i < level.blocks.length; i++) {
    const b = level.blocks[i]!;
    if (b.at > run.t) break;
    run.nextBlock = i + 1;
    // Un bloc de boss bascule le niveau en « attente de mise à mort » : à
    // partir de là, c'est la disparition du boss qui met fin au niveau, pas
    // l'horloge. La convention de nommage suffit — le moteur n'a aucune notion
    // de boss, et n'a pas besoin d'en avoir une.
    if (b.wave.startsWith('boss-')) {
      run.bossPending = true;
      run.bossSpawned = false;
      run.bossUid = 0;
    }
    // Décor collisionnable du bloc : astéroïdes et complexes militaires posés à
    // l'activation, une fois pour toutes. No-op si le bloc n'en porte aucun.
    placeObstacles(w, b);
    run.activeWave = b.wave;
    run.waveT = 0;
    run.waveSpawn = 0;
    /* ⚠️ La file d'attente est VIDÉE. Ses échéances sont relatives à `waveT`,
       qu'on vient de remettre à zéro : les conserver ferait réapparaître les
       spawns en retard de la vague précédente au fil de la nouvelle, à des
       instants qui n'ont plus aucun sens.

       Le cas se produit dès qu'un bloc en coupe un autre — ce que le monde 1
       évitait en calant chaque bloc à la seconde près sur la durée du
       précédent. C'est une propriété qu'aucun outil ne vérifiait et qui se
       perdait au premier monde écrit par quelqu'un d'autre. Le symptôme aurait
       été une poignée d'ennemis apparaissant pendant le silence d'avant-boss,
       c'est-à-dire l'annulation du seul effet de mise en scène du jeu. */
    run.queue.length = 0;
    w.events.push(Ev.WaveStarted, i, 0, 0, 0);
  }

  if (run.activeWave) {
    const wave = w.defs.waves.get(run.activeWave);
    if (wave) {
      const wPrev = run.waveT;
      run.waveT += DT;
      for (let i = run.waveSpawn; i < wave.spawns.length; i++) {
        const s = wave.spawns[i]!;
        if (s.at > run.waveT) break;
        run.waveSpawn = i + 1;
        // `count` ennemis espacés de `spacing` : on les programme tous d'un
        // coup dans la file d'attente plutôt que de les faire apparaître
        // ensemble, sinon un groupe se lit comme un mur au lieu d'une nuée.
        for (let k = 0; k < s.count; k++) {
          run.queue.push({ at: s.at + k * s.spacing, enemy: s.enemy, lane: s.lane });
        }
      }
      void wPrev;
    }
  }

  // File d'attente : on fait apparaître ce dont l'heure est venue.
  for (let i = run.queue.length - 1; i >= 0; i--) {
    const q = run.queue[i]!;
    if (q.at > run.waveT) continue;
    const slot = spawnFromDef(w, q.enemy, q.lane);
    // Le premier ennemi apparu pendant un bloc de boss EST le boss : ces vagues
    // n'en contiennent qu'un. On retient son `uid` — jamais son slot, qui se
    // recycle — pour que le HUD puisse suivre sa vie.
    if (run.bossPending && !run.bossSpawned && slot >= 0) {
      run.bossSpawned = true;
      run.bossUid = w.enemies.uid[slot]!;
      // L'apparition du boss est un ÉVÉNEMENT de mise en scène : stinger d'entrée,
      // bascule d'ambiance, barre de boss. Elle n'émettait rien jusqu'ici.
      w.events.push(Ev.BossSpawned, 0, w.enemies.x[slot]!, w.enemies.y[slot]!, 0);
    }
    // Retrait par échange avec le dernier : O(1), et l'ordre de la file n'a
    // aucune importance puisque chaque entrée porte sa propre échéance.
    const last = run.queue.length - 1;
    run.queue[i] = run.queue[last]!;
    run.queue.pop();
  }

  /* Fin de niveau.
     ⚠️ Deux conditions, et la seconde est indispensable : tuer le boss TERMINE
     le niveau. Sans elle, `levelSystem` ne connaît que des horloges — un joueur
     qui abat le Héraut en 45 s subirait ~50 s d'écran vide avant que la
     timeline arrive à son terme. La victoire doit être immédiate, sinon elle
     ne se ressent pas comme une victoire. */
  /* ⚠️ `bossSpawned` est la condition qui manquait, et son absence produisait
     la panne la plus coûteuse rencontrée jusqu'ici : le niveau se terminait à
     l'INSTANT où le bloc de boss s'ouvrait.

     Le raisonnement d'origine — « boss attendu et écran vide, donc boss mort »
     — ignorait qu'un bloc de boss commence par cinq secondes de silence. Écran
     vide, file d'attente vide : les trois conditions étaient réunies avant que
     le boss n'existe. Le monde passait au suivant, le joueur n'avait rien vu,
     et il en concluait que les boss se battaient tout seuls.

     Rien ne le signalait : la partie se déroulait, le score montait, les mondes
     s'enchaînaient. C'est le silence d'avant-boss — le seul effet de mise en
     scène du jeu — qui déclenchait sa propre annulation. */
  if (run.bossPending && run.bossSpawned && w.enemies.count === 0 && run.queue.length === 0) {
    run.finished = true;
    // Le boss abattu alimente son jalon. `worldsCleared`, lui, n'est PLUS
    // incrémenté ici : dans la structure à deux étages, finir un NIVEAU est un
    // palier franchi, et c'est la couche application qui décide qu'un MONDE est
    // franchi quand tombe son dernier niveau. Un niveau-étape qui compterait
    // pour un monde gonflerait les jalons.
    w.progress.bossesKilled++;
    return;
  }
  if (run.t >= level.duration && run.queue.length === 0) {
    // Fin par expiration de la timeline : le niveau (étape) est terminé. La
    // couche application enchaîne sur le niveau suivant du monde, ou clôt le
    // monde si c'était le dernier.
    run.finished = true;
  }
  void prev;
}

/** Coefficient de PV appliqué à un ennemi qui apparaît.
 *
 *  Produit de DEUX facteurs qui répondent à deux questions distinctes : quel
 *  MONDE on traverse (`run.hpScale`, fourni par la couche application au
 *  démarrage — dans la structure à deux étages, c'est le monde qui le porte,
 *  pas le niveau), et à quel palier on joue (`ENEMY_HP_MUL`). Le troisième —
 *  `balance.enemyHp` — est appliqué par les appelants, qui l'ont déjà sous la
 *  main. */
function hpScale(w: World): number {
  return w.run.hpScale * ENEMY_HP_MUL[clampDifficulty(w.run.difficulty)]!;
}

/** Fait apparaître l'ennemi `idx` à la position (x, y), avec ses coefficients de
 *  PV, son bouclier directionnel éventuel et ses parties destructibles.
 *
 *  C'est le CŒUR COMMUN du spawn de vague (`spawnFromDef`, à une position de
 *  couloir) et de l'engendrement en combat (`broodSystem`, à la position du
 *  porteur). L'extraire garantit qu'un œuf éclos hérite exactement des mêmes
 *  coefficients de monde, du même bouclier et des mêmes parties qu'un ennemi de
 *  vague — un engendré exempté du coefficient de monde tomberait aussi vite au
 *  monde 3 qu'au monde 1.
 *
 *  ⚠️ Il ne tire JAMAIS le RNG : un engendrement part d'une position
 *  déterministe (celle du porteur), et le seul tirage — l'ordonnée dispersée
 *  d'un spawn de vague — reste dans `spawnFromDef`, à sa place d'origine dans le
 *  flux aléatoire de gameplay.
 *
 *  Retourne le slot, ou -1 si le pool a saturé (déjà comptabilisé en overflow). */
export function spawnEnemyAt(w: World, idx: number, x: number, y: number): number {
  const def = w.defs.enemies[idx];
  if (!def) return -1;

  const b = w.defs.balance;
  const scale = hpScale(w);
  const slot = w.enemies.spawn(
    idx,
    x,
    y,
    def.hp * b.enemyHp * scale,
    // La hurtbox vaut 120 % du visuel (PLAN.md §6.2) : l'asymétrie se ressent
    // comme de la puissance, jamais comme de l'assistance.
    def.visualRadius * 1.2,
    def.score,
    def.emitter.firstDelay,
    def.telegraph,
  );
  if (slot < 0) {
    w.overflow.enemies++;
    return -1;
  }

  if (def.shieldedByParts) w.enemies.shielded[slot] = 1;
  /* Bouclier directionnel (E08). Il suit les MÊMES coefficients que le corps —
     même argument que pour les parties destructibles plus bas : un bouclier
     exempté du coefficient de monde tomberait aussi vite au monde 3 qu'au monde
     1, et l'ennemi cesserait d'imposer un angle d'attaque au moment précis où
     le joueur a assez de puissance de feu pour l'ignorer. */
  if (def.shieldHp !== undefined && def.shieldConeDeg !== undefined) {
    w.enemies.equipShield(slot, def.shieldHp * b.enemyHp * scale, def.shieldConeDeg * DEG);
  }
  if (def.parts) spawnParts(w, slot, def.parts);
  return slot;
}

/** Retourne le slot du porteur, ou -1 si le pool a saturé. */
function spawnFromDef(w: World, enemyId: string, lane: number): number {
  const idx = w.defs.enemyByI.get(enemyId);
  if (idx === undefined) return -1;
  // ⚠️ La garde sur `def` vient AVANT le tirage RNG, à l'identique de la version
  // d'origine : consommer le RNG puis renoncer décalerait tout le flux aléatoire
  // et invaliderait les golden replays.
  if (!w.defs.enemies[idx]) return -1;
  const y = PF_HH + 60 + w.rng.range(0, 90);
  return spawnEnemyAt(w, idx, laneX(lane), y);
}

/** Fait apparaître les parties d'un porteur et les lui rattache.
 *
 *  Une partie qui ne trouve pas de place est simplement absente : le porteur
 *  reste jouable, seulement moins armé. Mieux vaut un boss diminué qu'un boss
 *  invincible — car s'il est blindé par ses parties et qu'aucune n'existe,
 *  `livingParts` renvoie zéro et le noyau redevient vulnérable. Le système
 *  dégrade dans le bon sens. */
function spawnParts(w: World, parent: number, parts: readonly PartDef[]): void {
  const en = w.enemies;
  for (const part of parts) {
    const idx = w.defs.enemyByI.get(part.enemy);
    if (idx === undefined) continue;
    const pdef = w.defs.enemies[idx];
    if (!pdef) continue;

    const b = w.defs.balance;
    // ⚠️ Les parties suivent le MÊME coefficient que leur porteur. Les en
    // exempter rendrait un boss du monde 3 démontable aussi vite qu'au monde 1,
    // et son bouclier — donc sa structure de combat — s'effondrerait.
    const scale = hpScale(w);
    const slot = en.spawn(
      idx,
      en.x[parent]! + part.offX,
      en.y[parent]! + part.offY,
      pdef.hp * b.enemyHp * scale,
      pdef.visualRadius * 1.2,
      pdef.score,
      pdef.emitter.firstDelay,
      // Les parties héritent du télégraphe du porteur : elles apparaissent avec
      // lui, elles ne surgissent pas séparément.
      en.telegraph[parent]!,
    );
    if (slot < 0) {
      w.overflow.enemies++;
      return;
    }
    en.attach(slot, parent, part.offX, part.offY);
  }
}
